import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Drawer, FormControlLabel, IconButton, InputAdornment, LinearProgress, MenuItem,
  Paper, Snackbar, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  DataGridPro, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector,
  GridToolbarExport, GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import {
  Close, DeleteOutline, EditOutlined, InfoOutlined, KeyOutlined, LockOpenOutlined, LockOutlined,
  Refresh, Search, SecurityOutlined,
} from '@mui/icons-material';
import { Link as RouterLink, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Setup2FA from './Setup2FA';
import RegisterAccessDialog from './RegisterAccessDialog';
import { UserContext } from '../UserContext';
import { clearRegisterSession, isRegisterSessionCurrent } from '../utils/registerSession';

const ROLES = ['viewer', 'inspector', 'editor', 'reporting', 'admin'];
const apiUrl = (path) => `${process.env.REACT_APP_API_BASE_URL}${path}`;
const requestConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;
const displayName = (user) => [user.name, user.surname].filter(Boolean).join(' ') || user.username;
const normalizeUser = (user) => ({
  ...user,
  username: user.username || '', name: user.name || '', surname: user.surname || '',
  email: user.email || '', company_name: user.company_name || '',
  role: user.role || 'viewer', database_name: user.database_name || '',
  failed_attempts: Number(user.failed_attempts) || 0, is_blocked: user.is_blocked === true,
});

function UserToolbar() {
  return (
    <GridToolbarContainer sx={{ px: 2, py: 1, gap: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <Box sx={{ flex: 1 }} />
      <GridToolbarExport csvOptions={{ fileName: 'exreg-users', utf8WithBom: true }} />
    </GridToolbarContainer>
  );
}

function EmptyUsers() {
  return (
    <Stack sx={{ height: '100%', p: 3 }} alignItems="center" justifyContent="center" spacing={0.5}>
      <Typography fontWeight={600}>No users to display</Typography>
      <Typography variant="body2" color="text.secondary">Try another search or clear the filters.</Typography>
    </Stack>
  );
}

const UserManagement = () => {
  const { user: currentUser, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [databaseError, setDatabaseError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [toast, setToast] = useState(null);
  const [density, setDensity] = useState('standard');
  const [editingUser, setEditingUser] = useState(null);
  const [passwordUser, setPasswordUser] = useState(null);
  const [registerAccessUser, setRegisterAccessUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [setup2FAOpen, setSetup2FAOpen] = useState(false);
  const detailsId = searchParams.get('userId');
  const [registerFacts, setRegisterFacts] = useState({ loading: false, checklist: null, expiry: null });
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const requestVersion = useRef(0);
  const mutationInFlight = useRef(false);
  const mounted = useRef(false);

  const status = searchParams.get('status') || 'all';
  const role = searchParams.get('role') || 'all';
  const register = searchParams.get('register') || 'all';
  const query = searchParams.get('q') || '';
  const supervisor = searchParams.get('supervisor') || 'all';
  const detailUser = users.find((user) => String(user.id) === String(detailsId));
  const showToast = (message, severity = 'success') => setToast({ message, severity });

  const fetchData = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    setLoadError('');
    setDatabaseError('');
    if (!localStorage.getItem('token')) {
      setLoadError('Your session is unavailable. Please sign in as an administrator.');
      setLoading(false);
      return;
    }
    const [userResult, databaseResult] = await Promise.allSettled([
      axios.get(apiUrl('/api/admin/users'), requestConfig()),
      axios.get(apiUrl('/api/admin/databases'), requestConfig()),
    ]);
    if (requestVersion.current !== version) return;
    if (userResult.status === 'fulfilled' && Array.isArray(userResult.value.data)) {
      setUsers(userResult.value.data.map(normalizeUser));
      setUpdatedAt(new Date());
    } else {
      setLoadError('Could not refresh users. Any users shown are from the last successful refresh.');
    }
    if (databaseResult.status === 'fulfilled' && Array.isArray(databaseResult.value.data)) {
      setDatabases(databaseResult.value.data);
    } else {
      setDatabaseError('Register choices could not be refreshed. Register assignment is unavailable until refresh succeeds.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    mounted.current = true;
    fetchData();
    return () => { mounted.current = false; requestVersion.current += 1; };
  }, [fetchData]);

  const detailDatabase = detailUser?.database_name;
  const detailAccountId = detailUser?.id;
  useEffect(() => {
    if (!detailDatabase) {
      setRegisterFacts({ loading: false, checklist: null, expiry: null });
      return undefined;
    }
    const controller = new AbortController();
    setRegisterFacts({ loading: true, checklist: null, expiry: null });
    const base = `/api/settings/database/${encodeURIComponent(detailDatabase)}`;
    const config = { ...requestConfig(), signal: controller.signal };
    Promise.allSettled([
      axios.get(apiUrl(`${base}/checklist`), config),
      axios.get(apiUrl(`${base}/expiry`), config),
    ]).then(([checklistResult, expiryResult]) => {
      if (controller.signal.aborted) return;
      const checklist = checklistResult.status === 'fulfilled' && typeof checklistResult.value.data?.checklistEnabled === 'boolean' ? checklistResult.value.data : null;
      const expiry = expiryResult.status === 'fulfilled' && Object.prototype.hasOwnProperty.call(expiryResult.value.data || {}, 'expiryDate') ? expiryResult.value.data : null;
      setRegisterFacts({ loading: false, checklist, expiry, error: !checklist || !expiry });
    });
    return () => controller.abort();
  }, [detailDatabase, detailAccountId, updatedAt]);

  const setFilter = (name, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || (name !== 'q' && value === 'all')) next.delete(name);
    else next.set(name, value);
    setSearchParams(next, { replace: true });
  };
  const setDetailsId = (id) => setFilter('userId', id == null ? '' : String(id));
  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    ['q', 'status', 'role', 'register', 'supervisor'].forEach((name) => next.delete(name));
    setSearchParams(next, { replace: true });
  };
  const counts = useMemo(() => ({
    all: users.length,
    blocked: users.filter((user) => user.is_blocked).length,
    unverified: users.filter((user) => user.email_verified === false).length,
    supervisors: users.filter((user) => user.is_db_supervisor === true).length,
    unassigned: users.filter((user) => !user.database_name).length,
    failed: users.filter((user) => user.failed_attempts > 0).length,
    'admin-security': users.filter((user) => user.role === 'admin' && user.two_factor_enabled === false).length,
  }), [users]);
  const filteredUsers = useMemo(() => {
    const term = query.trim().toLowerCase();
    return users.filter((user) => {
      if (status === 'blocked' && !user.is_blocked) return false;
      if (status === 'unverified' && user.email_verified !== false) return false;
      if (status === 'unassigned' && user.database_name) return false;
      if (status === 'failed' && user.failed_attempts <= 0) return false;
      if (status === 'admin-security' && !(user.role === 'admin' && user.two_factor_enabled === false)) return false;
      if (supervisor === 'true' && user.is_db_supervisor !== true) return false;
      if (supervisor === 'false' && user.is_db_supervisor === true) return false;
      if (role !== 'all' && user.role !== role) return false;
      if (register !== 'all' && user.database_name !== register) return false;
      return !term || [user.id, user.username, user.name, user.surname, user.email, user.company_name, user.database_name]
        .some((value) => String(value ?? '').toLowerCase().includes(term));
    });
  }, [users, query, status, role, register, supervisor]);
  const registerNames = useMemo(() => [...new Set([
    ...databases.map((database) => database.datname), ...users.map((user) => user.database_name),
    register !== 'all' ? register : '',
  ].filter(Boolean))].sort(), [databases, users, register]);
  const roleNames = useMemo(() => [...new Set([...ROLES, ...users.map((user) => user.role), role !== 'all' ? role : ''].filter(Boolean))], [users, role]);
  const hasFilters = Boolean(query || status !== 'all' || role !== 'all' || register !== 'all' || supervisor !== 'all');

  const openEdit = (user) => { setActionError(''); setEditingUser({ ...user }); };
  const openPassword = (user) => { setActionError(''); setNewPassword(''); setPasswordUser(user); };
  const openConfirmation = (action, user) => { setActionError(''); setConfirmation({ action, user }); };
  const runMutation = async (operation, fallback) => {
    if (mutationInFlight.current) return;
    mutationInFlight.current = true;
    setBusy(true);
    setActionError('');
    try { await operation(); }
    catch (error) { if (mounted.current) setActionError(errorMessage(error, fallback)); }
    finally { mutationInFlight.current = false; if (mounted.current) setBusy(false); }
  };
  const saveUser = (event) => {
    event.preventDefault();
    if (!editingUser) return;
    const payload = {
      username: editingUser.username.trim(), name: editingUser.name.trim(), surname: editingUser.surname.trim(),
      email: editingUser.email.trim(), company_name: editingUser.company_name.trim(),
      role: editingUser.role, database_name: editingUser.database_name || null,
      is_db_supervisor: editingUser.is_db_supervisor === true,
    };
    if (!payload.username || !payload.email || !payload.company_name) {
      setActionError('Username, company email and company name are required.');
      return;
    }
    const token = localStorage.getItem('token');
    const requestStillCurrent = () => mounted.current && isRegisterSessionCurrent(token);
    runMutation(async () => {
      let response;
      try {
        response = await axios.put(apiUrl(`/api/admin/users/${editingUser.id}/update`), payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        if (!requestStillCurrent()) return;
        throw error;
      }
      // A late response must never sign out a replacement login or another register.
      if (!requestStillCurrent()) return;
      if (response.data?.reauthenticationRequired) {
        setEditingUser(null);
        setUser(null);
        navigate('/login', {
          replace: true,
          state: { message: response.data.message || 'User details updated. Please sign in again to continue with your updated access.' },
        });
        clearRegisterSession();
        return;
      }
      setUsers((previous) => previous.map((user) => user.id === editingUser.id ? normalizeUser({ ...user, ...payload, ...response.data?.user }) : user));
      setEditingUser(null);
      showToast('User details updated.');
    }, 'Could not update this user.');
  };
  const savePassword = (event) => {
    event.preventDefault();
    if (!passwordUser) return;
    if (!newPassword.trim()) { setActionError('Enter a new password.'); return; }
    runMutation(async () => {
      const response = await axios.post(apiUrl(`/api/admin/users/${passwordUser.id}/reset-password`), { newPassword }, requestConfig());
      if (response.data?.reauthenticationRequired) {
        setPasswordUser(null);
        setNewPassword('');
        setUser(null);
        navigate('/login', {
          replace: true,
          state: { message: response.data.message || 'Password updated. Please sign in again.' },
        });
        clearRegisterSession();
        return;
      }
      setUsers((previous) => previous.map((user) => user.id === passwordUser.id ? { ...user, is_blocked: false, failed_attempts: 0 } : user));
      setPasswordUser(null);
      setNewPassword('');
      showToast('Password updated and account unblocked.');
    }, 'Could not reset this password.');
  };
  const confirmAction = () => {
    if (!confirmation) return;
    const { action, user: selected } = confirmation;
    runMutation(async () => {
      if (action === 'delete') {
        await axios.delete(apiUrl(`/api/admin/users/${selected.id}/delete`), requestConfig());
        setUsers((previous) => previous.filter((user) => user.id !== selected.id));
        showToast('User deleted.');
      } else {
        await axios.post(apiUrl(`/api/admin/users/${selected.id}/${action}`), {}, requestConfig());
        setUsers((previous) => previous.map((user) => user.id === selected.id ? {
          ...user, is_blocked: action === 'block', failed_attempts: action === 'unblock' ? 0 : user.failed_attempts,
        } : user));
        showToast(`User ${action === 'block' ? 'blocked' : 'unblocked'}.`);
      }
      setConfirmation(null);
    }, 'Could not complete this action.');
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 65 },
    {
      field: 'username', headerName: 'Username', minWidth: 130, flex: 0.8,
      renderCell: ({ row }) => <Button size="small" onClick={() => setDetailsId(row.id)} sx={{ justifyContent: 'flex-start', px: 0, minWidth: 0, fontWeight: 600, textTransform: 'none', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.username || 'Unnamed user'}</Button>,
    },
    { field: 'full_name', headerName: 'Name', minWidth: 150, flex: 1, valueGetter: (_value, row) => displayName(row) },
    { field: 'name', headerName: 'First name', width: 130 },
    { field: 'surname', headerName: 'Last name', width: 140 },
    { field: 'email', headerName: 'Email address', minWidth: 210, flex: 1.3 },
    { field: 'company_name', headerName: 'Company', minWidth: 160, flex: 1 },
    { field: 'role', headerName: 'Role', width: 100 },
    { field: 'is_db_supervisor', headerName: 'Supervisor', width: 130, type: 'boolean',
      renderCell: ({ value }) => <Typography variant="body2" fontWeight={value ? 600 : 400} color={value ? 'primary.main' : 'text.secondary'}>{value === true ? 'Yes' : 'No'}</Typography>,
    },
    { field: 'database_name', headerName: 'Current register', minWidth: 160, flex: 1, renderCell: ({ value }) => value || <Typography variant="body2" color="text.secondary">Unassigned</Typography> },
    { field: 'is_blocked', headerName: 'Account', width: 130, type: 'boolean',
      renderCell: ({ row }) => <Chip size="small" label={row.is_blocked ? 'Blocked' : row.role === 'inactive' ? 'Inactive' : 'Unblocked'} color={row.is_blocked ? 'error' : 'default'} variant="outlined" sx={{ fontSize: 12, height: 22 }} />,
    },
    { field: 'email_verified', headerName: 'Email verified', width: 140, type: 'boolean',
      renderCell: ({ value }) => <Typography variant="body2" color={value === false ? 'warning.dark' : 'text.secondary'}>{value === true ? 'Yes' : value === false ? 'No' : 'Unknown'}</Typography>,
    },
    { field: 'failed_attempts', headerName: 'Failed logins', type: 'number', width: 130 },
    { field: 'two_factor_enabled', headerName: '2FA', width: 100, type: 'boolean',
      renderCell: ({ row, value }) => row.role === 'admin' ? (value === true ? 'Enabled' : value === false ? 'Not enabled' : 'Unknown') : '—',
    },
    {
      field: 'actions', headerName: 'Actions', width: 250, sortable: false, filterable: false, disableExport: true, disableColumnMenu: true,
      renderCell: ({ row }) => <Stack direction="row" alignItems="center" spacing={0.25} sx={{ height: '100%' }}>
        <Button size="small" startIcon={<EditOutlined fontSize="small" />} aria-label={`Edit ${row.username}`} onClick={() => openEdit(row)} disabled={busy || loading} sx={{ minWidth: 55 }}>Edit</Button>
        <Tooltip title="Account details"><span><IconButton size="small" aria-label={`Details for ${row.username}`} onClick={() => setDetailsId(row.id)}><InfoOutlined fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Reset password"><span><IconButton size="small" aria-label={`Reset password for ${row.username}`} onClick={() => openPassword(row)} disabled={busy || loading}><KeyOutlined fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title={row.is_blocked ? 'Unblock user' : 'Block user'}><span><IconButton size="small" aria-label={`${row.is_blocked ? 'Unblock' : 'Block'} ${row.username}`} onClick={() => openConfirmation(row.is_blocked ? 'unblock' : 'block', row)} disabled={busy || loading}>{row.is_blocked ? <LockOpenOutlined fontSize="small" /> : <LockOutlined fontSize="small" />}</IconButton></span></Tooltip>
        <Tooltip title="Delete user"><span><IconButton size="small" aria-label={`Delete ${row.username}`} onClick={() => openConfirmation('delete', row)} disabled={busy || loading}><DeleteOutline fontSize="small" /></IconButton></span></Tooltip>
      </Stack>,
    },
  ];
  const actionLabel = confirmation?.action === 'delete' ? 'Delete user' : confirmation?.action === 'block' ? 'Block user' : 'Unblock user';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', minWidth: 0, flex: 1, minHeight: 520 }}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" useFlexGap flexWrap="wrap" gap={1}>
          <Button startIcon={<SecurityOutlined />} onClick={() => setSetup2FAOpen(true)} disabled={busy}>My two-factor setup</Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData} disabled={loading || busy}>{loading ? 'Refreshing…' : 'Refresh'}</Button>
      </Stack>

      {loadError && <Alert severity="error" action={<Button color="inherit" size="small" onClick={fetchData} disabled={loading}>Retry</Button>}>{loadError}</Alert>}
      {databaseError && <Alert severity="warning">{databaseError}</Alert>}
      {detailsId && !detailUser && !loading && !loadError && <Alert severity="warning" onClose={() => setDetailsId(null)}>The requested user account is no longer available.</Alert>}
      <Paper className="admin-table-card" variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 480 }}>
        <Stack spacing={1} sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
            <TextField size="small" placeholder="Search users, company or email" value={query} onChange={(event) => setFilter('q', event.target.value)} inputProps={{ 'aria-label': 'Search users' }} InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} sx={{ flex: '1 1 220px', maxWidth: 380 }} />
            <TextField select size="small" label="Account status" value={status} onChange={(event) => setFilter('status', event.target.value)} sx={{ minWidth: 180 }}>
              {[
                ['all', 'All users'], ['blocked', 'Blocked'], ['unverified', 'Unverified email'], ['unassigned', 'Without register'],
                ['failed', 'Failed logins'], ['admin-security', 'Admins without 2FA'],
              ].map(([value, label]) => <MenuItem key={value} value={value}>{label} ({updatedAt ? counts[value] : '—'})</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Supervisor access" value={supervisor} onChange={(event) => setFilter('supervisor', event.target.value)} sx={{ minWidth: 155 }}>
              <MenuItem value="all">All users</MenuItem><MenuItem value="true">Supervisors ({counts.supervisors})</MenuItem><MenuItem value="false">Non-supervisors</MenuItem>
            </TextField>
            <TextField select size="small" label="Role" value={role} onChange={(event) => setFilter('role', event.target.value)} sx={{ minWidth: 120 }}>
              <MenuItem value="all">All roles</MenuItem>
              {roleNames.map((option) => <MenuItem key={option} value={option} sx={{ textTransform: 'capitalize' }}>{option}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Current register" value={register} onChange={(event) => setFilter('register', event.target.value)} sx={{ minWidth: 190, maxWidth: 290 }}>
              <MenuItem value="all">All registers</MenuItem>
              {registerNames.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
            </TextField>
            {hasFilters && <Button onClick={clearFilters} sx={{ whiteSpace: 'nowrap' }}>Clear filters</Button>}
          </Box>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="caption" color="text.secondary">{updatedAt ? `${filteredUsers.length} of ${users.length} users` : loading ? 'Loading user directory' : 'User directory unavailable'}</Typography>
            {updatedAt && <Typography variant="caption" color="text.secondary">Refreshed {updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography>}
          </Stack>
        </Stack>
        <Box sx={{ flex: 1, minHeight: 360 }}>
          <DataGridPro rows={filteredUsers} columns={columns} density={density} onDensityChange={setDensity} loading={loading}
            pagination pageSizeOptions={[25, 50, 100]} disableRowSelectionOnClick
            initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } }, columns: { columnVisibilityModel: { id: false, name: false, surname: false, company_name: false, two_factor_enabled: false } }, pinnedColumns: { right: ['actions'] } }}
            slots={{ toolbar: UserToolbar, noRowsOverlay: EmptyUsers, noResultsOverlay: EmptyUsers }}
            sx={{ border: 0, fontSize: 14, '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8fafc' }, '& .MuiDataGrid-cell': { borderColor: '#edf1f5' }, '& .MuiDataGrid-row:hover': { backgroundColor: '#f8fafc' }, '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 } }} />
        </Box>
      </Paper>

      <Drawer sx={{ zIndex: theme => theme.zIndex.drawer + 2 }} anchor="right" open={Boolean(detailUser)} onClose={() => { if (!busy) setDetailsId(null); }} PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, maxWidth: '100%' } }}>
        {detailUser && <Box role="dialog" aria-labelledby="user-details-title" sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box><Typography id="user-details-title" component="h2" sx={{ fontSize: 18, fontWeight: 600 }}>{detailUser.username}</Typography><Typography variant="body2" color="text.secondary">Account #{detailUser.id} · {displayName(detailUser)}</Typography></Box>
            <IconButton aria-label="Close account details" disabled={busy} onClick={() => setDetailsId(null)}><Close /></IconButton>
          </Stack>
          <Stack spacing={2} sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="contained" size="small" startIcon={<EditOutlined />} onClick={() => openEdit(detailUser)} disabled={busy || loading}>Edit user</Button>
              {currentUser?.role === 'admin' && <Button variant="outlined" size="small" disabled={busy || loading} onClick={() => setRegisterAccessUser(detailUser)}>Register access</Button>}
              <Button component={RouterLink} to={`/admin/logger?userId=${encodeURIComponent(detailUser.id)}`} size="small" variant="outlined">User audit history</Button>
              {detailUser.database_name && <Button component={RouterLink} to={`/admin/databases?q=${encodeURIComponent(detailUser.database_name)}`} size="small">Register settings</Button>}
            </Stack>
            {(detailUser.is_blocked || detailUser.email_verified === false || detailUser.role === 'inactive') && <Alert severity="warning" sx={{ fontSize: 14 }}>
              {detailUser.is_blocked ? 'This account is blocked. Existing sessions are denied access.' : detailUser.role === 'inactive' ? 'This account has an inactive role.' : 'This account must verify its email before signing in.'}
            </Alert>}
            <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: '130px minmax(0, 1fr)', columnGap: 2, rowGap: 1, '& dt': { color: 'text.secondary', fontSize: 14 }, '& dd': { m: 0, fontSize: 14, overflowWrap: 'anywhere' } }}>
              {[
                ['Email', detailUser.email || 'Not recorded'], ['Company', detailUser.company_name || 'Not recorded'],
                ['Role', detailUser.role], ['Register supervisor', detailUser.is_db_supervisor === true ? 'Yes' : 'No'],
                ['Current register', detailUser.database_name || 'Unassigned'], ['Account block', detailUser.is_blocked ? 'Blocked' : 'Not blocked'],
                ['Email verified', detailUser.email_verified === true ? 'Yes' : detailUser.email_verified === false ? 'No' : 'Unknown'],
                ['Failed sign-ins', detailUser.failed_attempts],
                ['Two-factor auth', detailUser.role === 'admin' ? detailUser.two_factor_enabled === true ? 'Enabled' : detailUser.two_factor_enabled === false ? 'Not enabled' : 'Unknown' : 'Only available for administrators'],
              ].map(([label, value]) => <React.Fragment key={label}><Box component="dt">{label}</Box><Box component="dd">{value}</Box></React.Fragment>)}
            </Box>
            <Divider />
            <Box>
              <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Register permissions</Typography>
              <Typography variant="body2" color="text.secondary">{detailUser.is_db_supervisor === true ? 'This supervisor can manage users in their assigned register.' : 'This account does not have register-supervisor privileges.'} {detailUser.role === 'admin' ? 'The admin role separately grants platform administration.' : 'Supervisor access does not change the account’s role.'}</Typography>
              {!detailUser.database_name ? <Alert severity="info" sx={{ mt: 1.5 }}>No register is assigned.</Alert> : registerFacts.loading ? <LinearProgress aria-label="Loading register permissions" sx={{ mt: 2 }} /> : <>
                {registerFacts.error && <Alert severity="warning" sx={{ mt: 1.5 }} action={<Button color="inherit" size="small" onClick={fetchData} disabled={loading || busy}>Retry</Button>}>Some register settings could not be verified.</Alert>}
                <Box component="dl" sx={{ m: 0, mt: 1.5, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 1, '& dt': { color: 'text.secondary', fontSize: 14 }, '& dd': { m: 0, fontSize: 14, textAlign: 'right' } }}>
                  <Box component="dt">Register expiry</Box><Box component="dd">{registerFacts.expiry ? registerFacts.expiry.expiryDate ? `${String(registerFacts.expiry.expiryDate).slice(0, 10)}${registerFacts.expiry.isExpired ? ' · Expired' : ''}` : 'Not set' : 'Unavailable'}</Box>
                  <Box component="dt">Inspection checklist</Box><Box component="dd">{registerFacts.checklist ? registerFacts.checklist.checklistEnabled ? 'Enabled' : 'Disabled' : 'Unavailable'}</Box>
                  <Box component="dt">Supervisor question editing</Box><Box component="dd">{typeof registerFacts.checklist?.supervisorEditEnabled === 'boolean' ? registerFacts.checklist.supervisorEditEnabled ? 'Enabled in register' : 'Disabled in register' : 'Unavailable'}</Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>Editing inspection questions requires supervisor access plus both register settings above. Granting supervisor access does not enable those settings.</Typography>
              </>}
            </Box>
            <Divider />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button size="small" variant="outlined" startIcon={<KeyOutlined />} disabled={busy || loading} onClick={() => openPassword(detailUser)}>Reset password</Button>
              <Button size="small" variant="outlined" startIcon={detailUser.is_blocked ? <LockOpenOutlined /> : <LockOutlined />} disabled={busy || loading} onClick={() => openConfirmation(detailUser.is_blocked ? 'unblock' : 'block', detailUser)}>{detailUser.is_blocked ? 'Unblock user' : 'Block user'}</Button>
              <Button size="small" color="error" startIcon={<DeleteOutline />} disabled={busy || loading} onClick={() => openConfirmation('delete', detailUser)}>Delete user</Button>
            </Stack>
          </Stack>
        </Box>}
      </Drawer>

      <Dialog open={Boolean(editingUser)} onClose={() => { if (!busy) setEditingUser(null); }} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={saveUser}>
          <DialogTitle>Edit user<Typography variant="body2" color="text.secondary">{editingUser?.username} · ID {editingUser?.id}</Typography></DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {actionError && <Alert severity="error">{actionError}</Alert>}
              <TextField label="Username" required size="small" value={editingUser?.username || ''} disabled={busy} onChange={(event) => setEditingUser({ ...editingUser, username: event.target.value })} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="First name" size="small" fullWidth value={editingUser?.name || ''} disabled={busy} onChange={(event) => setEditingUser({ ...editingUser, name: event.target.value })} />
                <TextField label="Last name" size="small" fullWidth value={editingUser?.surname || ''} disabled={busy} onChange={(event) => setEditingUser({ ...editingUser, surname: event.target.value })} />
              </Stack>
              <TextField label="Company email" type="email" required size="small" helperText="Use a company email address. Public email providers are not accepted." value={editingUser?.email || ''} disabled={busy} onChange={(event) => setEditingUser({ ...editingUser, email: event.target.value })} />
              <TextField label="Company" required size="small" value={editingUser?.company_name || ''} disabled={busy} onChange={(event) => setEditingUser({ ...editingUser, company_name: event.target.value })} />
              <TextField label="Role" select size="small" value={editingUser?.role || 'viewer'} disabled={busy} onChange={(event) => setEditingUser({ ...editingUser, role: event.target.value })}>
                {roleNames.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </TextField>
              <TextField label="Current register" select size="small" value={editingUser?.database_name || ''} disabled={busy || Boolean(databaseError)} helperText={databaseError || 'Sets the current register. Manage all permitted registers and their roles through Register access.'} onChange={(event) => setEditingUser({ ...editingUser, database_name: event.target.value, is_db_supervisor: false })}>
                <MenuItem value="">Unassigned</MenuItem>
                {registerNames.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
              </TextField>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <FormControlLabel sx={{ m: 0 }} control={<Checkbox size="small" checked={editingUser?.is_db_supervisor === true} disabled={busy || (!editingUser?.database_name && editingUser?.is_db_supervisor !== true)} onChange={(event) => setEditingUser({ ...editingUser, is_db_supervisor: event.target.checked })} />} label="Register supervisor" />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {editingUser?.database_name ? 'Can manage users in the assigned register. Inspection-question editing also requires the register’s checklist and supervisor editing settings to be enabled.' : 'Assign a register before granting supervisor access.'}
                </Typography>
                {editingUser?.database_name !== users.find((user) => user.id === editingUser?.id)?.database_name && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Changing register clears the supervisor selection. Select it again to explicitly grant access in the new register.</Typography>}
              </Box>
              {editingUser?.role === 'admin' && <Alert severity="info">Administrators can manage all users and registers. Grant this role only to platform administrators.</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}><Button disabled={busy} onClick={() => setEditingUser(null)}>Cancel</Button><Button variant="contained" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</Button></DialogActions>
        </Box>
      </Dialog>

      <RegisterAccessDialog open={Boolean(registerAccessUser)} account={registerAccessUser} onClose={() => setRegisterAccessUser(null)} onChanged={fetchData} />

      <Dialog open={Boolean(passwordUser)} onClose={() => { if (!busy) { setPasswordUser(null); setNewPassword(''); } }} fullWidth maxWidth="xs">
        <Box component="form" onSubmit={savePassword}>
          <DialogTitle>Reset password</DialogTitle>
          <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">Set a new password for <strong>{passwordUser?.username}</strong>.</Typography>
            <Alert severity="warning">This also unblocks the account and clears failed sign-in attempts.</Alert>
            {actionError && <Alert severity="error">{actionError}</Alert>}
            <TextField autoFocus label="New password" type="password" required fullWidth autoComplete="new-password" value={newPassword} disabled={busy} onChange={(event) => setNewPassword(event.target.value)} />
          </Stack></DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}><Button disabled={busy} onClick={() => { setPasswordUser(null); setNewPassword(''); }}>Cancel</Button><Button variant="contained" type="submit" disabled={busy || !newPassword.trim()}>{busy ? 'Resetting…' : 'Reset password'}</Button></DialogActions>
        </Box>
      </Dialog>

      {confirmation && <Dialog open onClose={() => { if (!busy) setConfirmation(null); }} fullWidth maxWidth="xs" aria-labelledby="user-action-title">
        <DialogTitle id="user-action-title">{actionLabel}?</DialogTitle>
        <DialogContent><Stack spacing={2}>
          <Typography variant="body2"><strong>{confirmation?.user.username}</strong><br />{confirmation?.user.email}</Typography>
          <Typography variant="body2" color="text.secondary">{confirmation?.action === 'delete' ? 'This permanently deletes the user account. This action cannot be undone.' : confirmation?.action === 'block' ? 'This prevents the user from signing in until the account is unblocked.' : 'This allows the user to sign in again and clears failed sign-in attempts.'}</Typography>
          {actionError && <Alert severity="error">{actionError}</Alert>}
        </Stack></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}><Button disabled={busy} onClick={() => setConfirmation(null)}>Cancel</Button><Button variant="contained" color={confirmation?.action === 'delete' ? 'error' : 'primary'} disabled={busy} onClick={confirmAction}>{busy ? 'Working…' : actionLabel}</Button></DialogActions>
      </Dialog>}

      <Dialog open={setup2FAOpen} onClose={() => setSetup2FAOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>My two-factor authentication</DialogTitle>
        <DialogContent><Alert severity="info" sx={{ mb: 2 }}>This setup applies to your currently signed-in administrator account.</Alert>{setup2FAOpen && <Setup2FA />}</DialogContent>
        <DialogActions><Button onClick={() => setSetup2FAOpen(false)}>Close</Button></DialogActions>
      </Dialog>
      <Snackbar open={Boolean(toast)} autoHideDuration={4500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={toast?.severity || 'success'} variant="filled" onClose={() => setToast(null)}>{toast?.message}</Alert>
      </Snackbar>
    </Box>
  );
};
export default UserManagement;
